import { Money } from '../components/ui/Money';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  UserSquare2, Plus, Pencil, Trash2, FileText, FileSpreadsheet, ReceiptText,
  History, Loader2, ArrowRight, ArrowLeft, X, MessageCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from '../hooks/use-toast';
import { shareHint } from '../utils/shareInfo';
import { SendReportButton } from '../components/common/SendReportButton';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });
const aedText = (v) => `AED ${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const aed = (v) => <Money value={v} digits={2} />;

const BASIS = {
  daily: { label: 'Daily', unit: 'days', rateLabel: 'Daily Rate', qtyLabel: 'No. of days worked' },
  weekly: { label: 'Weekly', unit: 'weeks', rateLabel: 'Weekly Rate', qtyLabel: 'No. of weeks worked' },
  monthly: { label: 'Monthly', unit: 'days', rateLabel: 'Monthly Salary', qtyLabel: 'No. of days worked (prorated)' },
  per_trip: { label: 'Per-Trip', unit: 'trips', rateLabel: 'Per-Trip Rate', qtyLabel: 'No. of trips' },
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const emptyForm = { name: '', designation: '', pay_basis: 'daily', default_rate: '', mobile: '', emirates_id: '', bank_name: '', account_no: '', notes: '' };

// Reliable download: fetch as blob (auth header) → typed Blob + anchor[download].
// (window.open on an untyped blob renders raw PDF/XLSX bytes as text in a tab.)
const downloadPayslip = async (ptId, psId, format, filename) => {
  try {
    const r = await axios.get(`${API}/api/part-timers/${ptId}/payslips/${psId}/download?format=${format}`, {
      headers: authHeaders(), responseType: 'blob',
    });
    const mime = format === 'excel'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/pdf';
    const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    toast({ title: 'Download failed', variant: 'destructive' });
  }
};

// One-tap WhatsApp: mint a public payslip link and open wa.me with a prefilled message.
const sharePayslipWhatsApp = async (ptId, ps) => {
  try {
    const r = await axios.post(`${API}/api/part-timers/${ptId}/payslips/${ps.id}/share-link`, {}, {
      headers: authHeaders(),
    });
    const { token: shareToken, mobile, name, period_label, net } = r.data || {};
    const url = `${API}/api/part-timers/public/payslip/${shareToken}?format=pdf`;
    const msg = `Hi ${name || ''}, here is your payslip for ${period_label}.\n`
      + `Net Pay: AED ${Number(net || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.\n`
      + `View / download: ${url}`;
    const digits = String(mobile || '').replace(/[^0-9]/g, '');
    const waUrl = digits
      ? `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    if (!digits) {
      toast({ title: 'No mobile on file', description: 'Opening WhatsApp without a number — pick the contact manually.' });
    }
    window.open(waUrl, '_blank');
    return r.data || null;
  } catch {
    toast({ title: 'Could not create WhatsApp link', variant: 'destructive' });
    return null;
  }
};

const PartTimers = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  // Issue payslip
  const [payOpen, setPayOpen] = useState(false);
  const [paySubject, setPaySubject] = useState(null); // the part-timer
  const [payStep, setPayStep] = useState(1);
  const now = new Date();
  const [pay, setPay] = useState({ pay_basis: 'daily', rate: '', quantity: '', cc_tips: '', deductions: '', deduction_note: '', period_month: now.getMonth() + 1, period_year: now.getFullYear(), notes: '' });
  const [workedDates, setWorkedDates] = useState([]); // Date[] — specific days worked (daily / per-trip)
  const [issuing, setIssuing] = useState(false);

  // History
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySubject, setHistorySubject] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [psDelete, setPsDelete] = useState(null);

  // Bulk WhatsApp payslips
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkYear, setBulkYear] = useState(now.getFullYear());
  const [bulkMonth, setBulkMonth] = useState(now.getMonth() + 1);
  const [bulkItems, setBulkItems] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const loadBulk = async () => {
    setBulkLoading(true); setBulkItems(null);
    try {
      const r = await axios.get(`${API}/api/part-timers/bulk/whatsapp-payslips?year=${bulkYear}&month=${bulkMonth}`, { headers: authHeaders() });
      setBulkItems(r.data?.items || []);
    } catch {
      toast({ title: 'Failed to load payslips', variant: 'destructive' });
      setBulkItems([]);
    } finally { setBulkLoading(false); }
  };

  const sendBulkRow = (it) => {
    const url = `${API}/api/part-timers/public/payslip/${it.token}?format=pdf`;
    const msg = `Hi ${it.name || ''}, here is your payslip for ${it.period_label}.\n`
      + `Net Pay: AED ${Number(it.net || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.\n`
      + `View / download: ${url}`;
    const digits = String(it.mobile || '').replace(/[^0-9]/g, '');
    if (!digits) toast({ title: 'No mobile on file', description: `${it.name}: pick the contact manually.` });
    window.open(digits ? `https://wa.me/${digits}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/part-timers`, { headers: authHeaders() });
      setList(r.data || []);
    } catch {
      toast({ title: 'Failed to load part-timers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // ── Directory form ──
  const openAdd = () => { setEditing(null); setForm(emptyForm); setFormOpen(true); };
  const openEdit = (pt) => {
    setEditing(pt);
    setForm({ ...emptyForm, ...pt, default_rate: pt.default_rate ?? '' });
    setFormOpen(true);
  };
  const saveForm = async () => {
    if (!form.name.trim()) { toast({ title: 'Name is required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const body = { ...form, default_rate: parseFloat(form.default_rate) || 0 };
      if (editing) await axios.put(`${API}/api/part-timers/${editing.id}`, body, { headers: authHeaders() });
      else await axios.post(`${API}/api/part-timers`, body, { headers: authHeaders() });
      toast({ title: editing ? 'Part-timer updated' : 'Part-timer added' });
      setFormOpen(false);
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  const doDelete = async () => {
    const pt = deleteTarget; setDeleteTarget(null);
    try {
      await axios.delete(`${API}/api/part-timers/${pt.id}`, { headers: authHeaders() });
      toast({ title: 'Part-timer removed' });
      await load();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  // ── Issue payslip ──
  const openIssue = (pt) => {
    setPaySubject(pt);
    setPayStep(1);
    setWorkedDates([]);
    setPay({
      pay_basis: pt.pay_basis || 'daily',
      rate: pt.default_rate ? String(pt.default_rate) : '',
      quantity: '', cc_tips: '', deductions: '', deduction_note: '',
      period_month: now.getMonth() + 1, period_year: now.getFullYear(), notes: '',
    });
    setPayOpen(true);
  };

  // ISO "YYYY-MM-DD" from a local Date (avoids UTC off-by-one)
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const usesDates = pay.pay_basis === 'daily' || pay.pay_basis === 'per_trip';
  // Picking dates auto-fills the count (still editable afterwards)
  const onPickDates = (dates) => {
    const arr = dates || [];
    setWorkedDates(arr);
    setPay((p) => ({ ...p, quantity: String(arr.length) }));
  };

  const previewCalc = () => {
    const rate = parseFloat(pay.rate) || 0;
    const qty = parseFloat(pay.quantity) || 0;
    const tips = parseFloat(pay.cc_tips) || 0;
    const ded = parseFloat(pay.deductions) || 0;
    let base;
    if (pay.pay_basis === 'monthly') {
      const dim = new Date(pay.period_year, pay.period_month, 0).getDate();
      base = dim ? (rate / dim) * qty : 0;
    } else {
      base = rate * qty;
    }
    const gross = base + tips;
    return { base, gross, net: gross - ded };
  };

  const submitIssue = async () => {
    if (!(parseFloat(pay.rate) > 0)) { toast({ title: 'Enter a valid rate', variant: 'destructive' }); return; }
    if (!(parseFloat(pay.quantity) > 0)) { toast({ title: 'Enter the quantity worked', variant: 'destructive' }); return; }
    setIssuing(true);
    try {
      const body = {
        pay_basis: pay.pay_basis,
        rate: parseFloat(pay.rate),
        quantity: parseFloat(pay.quantity),
        cc_tips: parseFloat(pay.cc_tips) || 0,
        deductions: parseFloat(pay.deductions) || 0,
        deduction_note: pay.deduction_note,
        period_month: Number(pay.period_month),
        period_year: Number(pay.period_year),
        notes: pay.notes,
        worked_dates: usesDates ? workedDates.map(toISO) : [],
      };
      const r = await axios.post(`${API}/api/part-timers/${paySubject.id}/payslips`, body, { headers: authHeaders() });
      toast({ title: 'Payslip issued', description: `${paySubject.name} — ${r.data.period_label} · Net ${aedText(r.data.net)}` });
      setPayOpen(false);
      const fname = `Payslip_${(paySubject.name || '').replace(/ /g, '_')}_${r.data.period_label.replace(/ /g, '_')}.pdf`;
      downloadPayslip(paySubject.id, r.data.id, 'pdf', fname);
      await load();
    } catch (e) {
      toast({ title: 'Failed to issue payslip', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setIssuing(false);
    }
  };

  // ── History ──
  const openHistory = async (pt) => {
    setHistorySubject(pt);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/api/part-timers/${pt.id}/payslips`, { headers: authHeaders() });
      setPayslips(r.data || []);
    } catch {
      setPayslips([]);
    } finally {
      setHistoryLoading(false);
    }
  };
  const deletePayslip = async () => {
    const ps = psDelete; setPsDelete(null);
    try {
      await axios.delete(`${API}/api/part-timers/${historySubject.id}/payslips/${ps.id}`, { headers: authHeaders() });
      setPayslips((prev) => prev.filter((p) => p.id !== ps.id));
      toast({ title: 'Payslip deleted' });
      await load();
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const years = [now.getFullYear() + 1, now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const calc = previewCalc();
  const meta = BASIS[pay.pay_basis] || BASIS.daily;

  return (
    <div className="space-y-6" data-testid="part-timers-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserSquare2 className="h-7 w-7 text-indigo-600" /> Part-Timers
          </h1>
          <p className="text-sm text-gray-500">Directory of part-time staff with manual payslip issuance (daily / weekly / monthly / per-trip).</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="border-green-300 text-green-700 hover:bg-green-50 gap-1.5" data-testid="bulk-whatsapp-btn">
            <MessageCircle className="h-4 w-4" /> WhatsApp Payslips
          </Button>
          <Button onClick={openAdd} className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="add-part-timer-btn">
            <Plus className="h-4 w-4" /> Add Part-Timer
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Directory ({list.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-x-auto">
            {loading ? (
              <div className="py-10 text-center text-gray-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
            ) : list.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm" data-testid="part-timers-empty">No part-timers yet. Click "Add Part-Timer" to start.</div>
            ) : (
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                  <tr>
                    <th className="text-left p-2.5">Name</th>
                    <th className="text-left p-2.5">Designation</th>
                    <th className="text-left p-2.5">Basis</th>
                    <th className="text-right p-2.5">Default Rate</th>
                    <th className="text-left p-2.5">Mobile</th>
                    <th className="text-center p-2.5">Payslips</th>
                    <th className="text-right p-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((pt) => (
                    <tr key={pt.id} className="border-t hover:bg-gray-50/50" data-testid={`part-timer-row-${pt.id}`}>
                      <td className="p-2.5 font-medium text-gray-900">{pt.name}</td>
                      <td className="p-2.5 text-gray-600">{pt.designation || '—'}</td>
                      <td className="p-2.5"><Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">{BASIS[pt.pay_basis]?.label || pt.pay_basis}</Badge></td>
                      <td className="p-2.5 text-right font-mono">{pt.default_rate ? aed(pt.default_rate) : '—'}</td>
                      <td className="p-2.5 text-gray-600">{pt.mobile || '—'}</td>
                      <td className="p-2.5 text-center">
                        <button onClick={() => openHistory(pt)} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-700" data-testid={`part-timer-history-${pt.id}`}>
                          <History className="h-3.5 w-3.5" /> {pt.payslip_count || 0}
                        </button>
                      </td>
                      <td className="p-2.5 text-right whitespace-nowrap">
                        <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 gap-1 mr-1" onClick={() => openIssue(pt)} data-testid={`part-timer-issue-${pt.id}`}>
                          <ReceiptText className="h-3.5 w-3.5" /> Issue Payslip
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(pt)} data-testid={`part-timer-edit-${pt.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => setDeleteTarget(pt)} data-testid={`part-timer-delete-${pt.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg" data-testid="part-timer-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Part-Timer' : 'Add Part-Timer'}</DialogTitle>
            <DialogDescription>Basic details kept in the Part-Timers directory.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="pt-form-name" />
            </div>
            <div>
              <Label className="text-xs">Designation</Label>
              <Input value={form.designation} onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))} placeholder="e.g. Food Runner" data-testid="pt-form-designation" />
            </div>
            <div>
              <Label className="text-xs">Pay Basis</Label>
              <Select value={form.pay_basis} onValueChange={(v) => setForm((f) => ({ ...f, pay_basis: v }))}>
                <SelectTrigger data-testid="pt-form-basis"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(BASIS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Default Rate (AED)</Label>
              <Input type="number" value={form.default_rate} onChange={(e) => setForm((f) => ({ ...f, default_rate: e.target.value }))} placeholder="e.g. 60" data-testid="pt-form-rate" />
            </div>
            <div>
              <Label className="text-xs">Mobile</Label>
              <Input value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} data-testid="pt-form-mobile" />
            </div>
            <div>
              <Label className="text-xs">Emirates ID (optional)</Label>
              <Input value={form.emirates_id} onChange={(e) => setForm((f) => ({ ...f, emirates_id: e.target.value }))} data-testid="pt-form-eid" />
            </div>
            <div>
              <Label className="text-xs">Bank Name (optional)</Label>
              <Input value={form.bank_name} onChange={(e) => setForm((f) => ({ ...f, bank_name: e.target.value }))} data-testid="pt-form-bank" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Account No / IBAN (optional)</Label>
              <Input value={form.account_no} onChange={(e) => setForm((f) => ({ ...f, account_no: e.target.value }))} data-testid="pt-form-account" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} data-testid="pt-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={saveForm} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="pt-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} {editing ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue payslip dialog (2-step) */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="issue-payslip-dialog">
          <DialogHeader>
            <DialogTitle>Issue Payslip — {paySubject?.name}</DialogTitle>
            <DialogDescription>Step {payStep} of 2 · {paySubject?.designation || 'Part-Timer'}</DialogDescription>
          </DialogHeader>

          {payStep === 1 ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Pay Basis</Label>
                <Select value={pay.pay_basis} onValueChange={(v) => setPay((p) => ({ ...p, pay_basis: v }))}>
                  <SelectTrigger data-testid="issue-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(BASIS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{meta.rateLabel} (AED)</Label>
                <Input type="number" value={pay.rate} onChange={(e) => setPay((p) => ({ ...p, rate: e.target.value }))} placeholder="Enter amount" autoFocus data-testid="issue-rate" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Period Month</Label>
                  <Select value={String(pay.period_month)} onValueChange={(v) => setPay((p) => ({ ...p, period_month: Number(v) }))}>
                    <SelectTrigger data-testid="issue-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Period Year</Label>
                  <Select value={String(pay.period_year)} onValueChange={(v) => setPay((p) => ({ ...p, period_year: Number(v) }))}>
                    <SelectTrigger data-testid="issue-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {usesDates && (
                <div data-testid="issue-dates-block">
                  <Label className="text-xs">Dates worked in {MONTHS[pay.period_month - 1]} {pay.period_year}</Label>
                  <div className="rounded-md border mt-1 flex justify-center">
                    <Calendar
                      mode="multiple"
                      month={new Date(pay.period_year, pay.period_month - 1, 1)}
                      fromMonth={new Date(pay.period_year, pay.period_month - 1, 1)}
                      toMonth={new Date(pay.period_year, pay.period_month - 1, 1)}
                      disabled={{
                        before: new Date(pay.period_year, pay.period_month - 1, 1),
                        after: new Date(pay.period_year, pay.period_month, 0),
                      }}
                      selected={workedDates}
                      onSelect={onPickDates}
                      data-testid="issue-calendar"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1" data-testid="issue-dates-count">
                    {workedDates.length > 0
                      ? `${workedDates.length} ${meta.unit} selected — count auto-filled below (editable)`
                      : `Tap the days worked. The count fills in automatically — you can still adjust it.`}
                  </p>
                </div>
              )}
              <div>
                <Label className="text-xs">{meta.qtyLabel}</Label>
                <Input type="number" value={pay.quantity} onChange={(e) => setPay((p) => ({ ...p, quantity: e.target.value }))} placeholder={`Number of ${meta.unit}`} autoFocus={!usesDates} data-testid="issue-quantity" />
              </div>
              <div>
                <Label className="text-xs">CC Tips (optional)</Label>
                <Input type="number" value={pay.cc_tips} onChange={(e) => setPay((p) => ({ ...p, cc_tips: e.target.value }))} placeholder="Leave blank if none" data-testid="issue-tips" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Deduction (optional)</Label>
                  <Input type="number" value={pay.deductions} onChange={(e) => setPay((p) => ({ ...p, deductions: e.target.value }))} placeholder="0" data-testid="issue-deduction" />
                </div>
                <div>
                  <Label className="text-xs">Deduction note</Label>
                  <Input value={pay.deduction_note} onChange={(e) => setPay((p) => ({ ...p, deduction_note: e.target.value }))} placeholder="Reason" data-testid="issue-deduction-note" />
                </div>
              </div>
              {/* Live preview */}
              <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-1" data-testid="issue-preview">
                <div className="flex justify-between"><span className="text-gray-500">Earnings ({(parseFloat(pay.quantity) || 0)} {meta.unit})</span><span className="font-mono">{aed(calc.base)}</span></div>
                {parseFloat(pay.cc_tips) > 0 && <div className="flex justify-between"><span className="text-gray-500">CC Tips</span><span className="font-mono text-blue-700">{aed(pay.cc_tips)}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Gross</span><span className="font-mono text-emerald-700">{aed(calc.gross)}</span></div>
                {parseFloat(pay.deductions) > 0 && <div className="flex justify-between"><span className="text-gray-500">Deductions</span><span className="font-mono text-rose-700">- {aed(pay.deductions)}</span></div>}
                <div className="flex justify-between border-t pt-1 font-semibold"><span>Net Pay</span><span className="font-mono">{aed(calc.net)}</span></div>
              </div>
            </div>
          )}

          <DialogFooter>
            {payStep === 1 ? (
              <>
                <Button variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
                <Button onClick={() => setPayStep(2)} disabled={!(parseFloat(pay.rate) > 0)} className="bg-indigo-600 hover:bg-indigo-700 gap-1" data-testid="issue-next">
                  Next <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setPayStep(1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={submitIssue} disabled={issuing} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="issue-submit">
                  {issuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />} Issue & Download
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl" data-testid="payslip-history-dialog">
          <DialogHeader>
            <DialogTitle>Payslip History — {historySubject?.name}</DialogTitle>
            <DialogDescription>Issued payslips. Re-download as PDF or Excel.</DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg overflow-x-auto max-h-[420px] overflow-y-auto">
            {historyLoading ? (
              <div className="py-8 text-center text-gray-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
            ) : payslips.length === 0 ? (
              <div className="py-8 text-center text-gray-400 text-sm">No payslips issued yet.</div>
            ) : (
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 sticky top-0">
                  <tr>
                    <th className="text-left p-2.5">Period</th>
                    <th className="text-left p-2.5">Basis</th>
                    <th className="text-right p-2.5">Qty</th>
                    <th className="text-right p-2.5">Gross</th>
                    <th className="text-right p-2.5">Net</th>
                    <th className="text-right p-2.5">Download</th>
                  </tr>
                </thead>
                <tbody>
                  {payslips.map((ps) => (
                    <tr key={ps.id} className="border-t" data-testid={`payslip-row-${ps.id}`}>
                      <td className="p-2.5 font-medium">{ps.period_label}</td>
                      <td className="p-2.5"><Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">{BASIS[ps.pay_basis]?.label || ps.pay_basis}</Badge></td>
                      <td className="p-2.5 text-right">{ps.quantity} {BASIS[ps.pay_basis]?.unit}</td>
                      <td className="p-2.5 text-right font-mono text-emerald-700">{aed(ps.gross)}</td>
                      <td className="p-2.5 text-right font-mono font-semibold">{aed(ps.net)}</td>
                      <td className="p-2.5 text-right whitespace-nowrap">
                        {ps.share_count > 0 && (
                          <span className="block text-[10px] text-gray-400 mb-1" data-testid={`payslip-share-hint-${ps.id}`}>
                            {shareHint(ps)}
                          </span>
                        )}
                        <SendReportButton
                          reportUrl={`/api/part-timers/${historySubject?.id}/payslips/${ps.id}/download?format=pdf`}
                          filename={`Payslip_${(historySubject?.name||'').replace(/ /g,'_')}_${ps.period_label.replace(/ /g,'_')}.pdf`}
                          defaultSubject={`Payslip — ${historySubject?.name} — ${ps.period_label}`}
                          defaultMessage={`Please find attached the payslip for ${historySubject?.name} (${ps.designation || 'Part-Timer'}) for ${ps.period_label}.\n\nNet Pay: AED ${Number(ps.net||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}.`}
                          iconOnly
                          size="sm"
                          className="h-7 w-7 p-0 mr-1 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                          testid={`payslip-email-${ps.id}`}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 w-7 p-0 mr-1 text-green-600 border-green-200 hover:bg-green-50"
                          onClick={async () => {
                            const d = await sharePayslipWhatsApp(historySubject.id, ps);
                            if (d) setPayslips((prev) => prev.map((p) => p.id === ps.id ? { ...p, share_count: d.share_count, last_shared_at: d.last_shared_at } : p));
                          }}
                          title="Send via WhatsApp"
                          data-testid={`payslip-whatsapp-${ps.id}`}
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 mr-1" onClick={() => downloadPayslip(historySubject.id, ps.id, 'pdf', `Payslip_${(historySubject.name||'').replace(/ /g,'_')}_${ps.period_label.replace(/ /g,'_')}.pdf`)} data-testid={`payslip-pdf-${ps.id}`}>
                          <FileText className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 px-2 mr-1" onClick={() => downloadPayslip(historySubject.id, ps.id, 'excel', `Payslip_${(historySubject.name||'').replace(/ /g,'_')}_${ps.period_label.replace(/ /g,'_')}.xlsx`)} data-testid={`payslip-excel-${ps.id}`}>
                          <FileSpreadsheet className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600" onClick={() => setPsDelete(ps)} data-testid={`payslip-delete-${ps.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp payslips */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-green-600" /> WhatsApp Payslips</DialogTitle>
          </DialogHeader>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-500">Month</label>
              <select value={bulkMonth} onChange={(e) => setBulkMonth(Number(e.target.value))} className="w-full border rounded-md h-9 px-2 text-sm" data-testid="bulk-month">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div className="w-28">
              <label className="text-xs text-gray-500">Year</label>
              <Input type="number" value={bulkYear} onChange={(e) => setBulkYear(Number(e.target.value))} className="h-9" data-testid="bulk-year" />
            </div>
            <Button onClick={loadBulk} disabled={bulkLoading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="bulk-load-btn">
              {bulkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load'}
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto mt-2">
            {bulkItems === null ? (
              <p className="text-sm text-gray-400 py-6 text-center">Pick a month and click Load.</p>
            ) : bulkItems.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center" data-testid="bulk-empty">No payslips issued for this month.</p>
            ) : (
              <ul className="divide-y">
                {bulkItems.map((it) => (
                  <li key={it.payslip_id} className="flex items-center justify-between py-2" data-testid={`bulk-row-${it.payslip_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{it.name}</p>
                      <p className="text-[11px] text-gray-400">{it.mobile || 'No mobile'} · AED {Number(it.net || 0).toLocaleString()}</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-8 border-green-300 text-green-700 hover:bg-green-50 gap-1" onClick={() => sendBulkRow(it)} data-testid={`bulk-send-${it.payslip_id}`}>
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete part-timer confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-part-timer-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove part-timer?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete <b>{deleteTarget?.name}</b> and all their issued payslips. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete payslip confirm */}
      <AlertDialog open={!!psDelete} onOpenChange={(o) => { if (!o) setPsDelete(null); }}>
        <AlertDialogContent data-testid="delete-payslip-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete payslip?</AlertDialogTitle>
            <AlertDialogDescription>Delete the {psDelete?.period_label} payslip record? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deletePayslip} className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PartTimers;
